import { Link } from 'gatsby';
import React, { useRef } from 'react';
import { connectInfiniteHits } from 'react-instantsearch-dom';
import { IconCloud, IconDownload, IconEnter, IconHardware, IconLearn, IconPaperStandard, IconProgramming, IconSoftware } from '../Icons';

import './SearchBarHits.scss';
import {
  ALL_DOCS,
  CLOUD_PATH,
  EXAMPLES_PATH,
  HARDWARE_PATH,
  LEARN_PATH,
  MYCROPYTHON_PATH,
  PRODUCT_PATH,
  SIDEBAR_CLOUD,
  SIDEBAR_MYCROPYTHON,
  SOFTWARE_PATH,
} from '../../utils/constants';
import classnames from 'classnames';
import { ActualPageProps } from '../../entities/Page';
import { LIMIT_HITS_PANEL } from './SearchBar';

interface SearchBarHitsProps {
  hits: any;
  hasMore: boolean;
  saveRecentSearch: (val: any) => void;
  selectedPanelItem: number;
  setSelectedPanelSlug: (val: string) => void;
  actualPage: ActualPageProps;
  selectedPage: string;
}

interface SearchBarHitProps {
  hit: any;
  saveRecentSearch: (val: any) => void;
  selectedPanelItem: number;
  index: number;
  setSelectedPanelSlug: (val: string) => void;
  showBreadcrumbs: boolean;
}

const SearchBarHit = ({ hit, saveRecentSearch, selectedPanelItem, index, setSelectedPanelSlug, showBreadcrumbs }: SearchBarHitProps) => {
  const cardRef = useRef(null);

  const getIcon = (slug: string) => {
    switch (slug) {
      case PRODUCT_PATH:
      case HARDWARE_PATH:
        return <IconHardware />;
      case SOFTWARE_PATH:
        return <IconSoftware />;
      case CLOUD_PATH:
        return <IconCloud />;
      case MYCROPYTHON_PATH:
      case EXAMPLES_PATH:
        return <IconProgramming />;
      case LEARN_PATH:
        return <IconLearn />;
      default:
        return <IconPaperStandard />; //Standard Icon
    }
  };

  const getDocumentationType = (documentationType: string) => {
    switch (documentationType) {
      case 'Arduino-cloud':
        return SIDEBAR_CLOUD;
      case 'Micropython-course':
        return SIDEBAR_MYCROPYTHON;
      default:
        return documentationType; //Standard Icon
    }
  };

  if (selectedPanelItem === index) {
    setSelectedPanelSlug(hit.slug);
  }

  // Components
  //console.log('---', hit);

  if (hit.pinout || hit.datasheet || hit.schematic) {
    return (
      <>
        <Link
          to={hit.slug}
          ref={cardRef}
          onClick={() => saveRecentSearch(hit)}
          className={classnames('search-hits', {
            'search-hits--selected': selectedPanelItem === index,
          })}
        >
          <div className="search-hits__img">{hit.slug ? getIcon(hit.slug.split('/')[1]) : getIcon(null)}</div>
          <div className="search-hits__content">
            <div className="search-hits__title">{hit.title}</div>
            <div className="search-hits__description">{hit.description}</div>
            {/* FIXME - use component */}
            {showBreadcrumbs && hit.slug?.split('/')[1] !== PRODUCT_PATH && hit.slug?.split('/')[1] !== SOFTWARE_PATH && (
              <div className="search-hits__breadcrumbs">
                Home
                <span className="search-hits__breadcrumbs--separator">{' / '}</span>
                {hit.breadcrumbObj?.map((breadcrumb, i) => (
                  <span key={breadcrumb.slug}>
                    {breadcrumb.title === '' ? getDocumentationType(hit.documentation_type) : breadcrumb.title}{' '}
                    <span className="search-hits__breadcrumbs--separator">{'/ '}</span>
                    {i === hit.breadcrumbObj.length - 1 && hit.title}
                  </span>
                )) || hit.title}
              </div>
            )}
            {(hit.slug?.split('/')[1] === PRODUCT_PATH || hit.slug?.split('/')[1] === SOFTWARE_PATH) && (
              <div className="search-hits__breadcrumbs">
                <span>
                  Home
                  <span className="search-hits__breadcrumbs--separator">{' / '}</span>
                  {hit.slug?.split('/')[1] === PRODUCT_PATH ? 'Hardware' : 'Software'}
                  <span className="search-hits__breadcrumbs--separator">{' / '}</span>
                  {hit.title}
                </span>
              </div>
            )}
          </div>
          {selectedPanelItem === index && (
            <div className="search-hits__enter">
              <IconEnter />
            </div>
          )}
        </Link>
        {hit.pinout && (
          <a href={hit.pinout} ref={cardRef} className={'search-hits'} download target="_blank" rel="noreferrer">
            <div className="search-hits__img">{hit.slug ? getIcon(hit.slug.split('/')[1]) : getIcon(null)}</div>
            <div className="search-hits__content">
              <div className="search-hits__title">{hit.title} - Pinout</div>
              <div className="search-hits__description">{hit.description}</div>
              <div className="search-hits__breadcrumbs">
                <span>
                  Home
                  <span className="search-hits__breadcrumbs--separator">{' / '}</span>
                  Hardware
                  <span className="search-hits__breadcrumbs--separator">{' / '}</span>
                  {hit.title}
                </span>
              </div>
            </div>
            <div className="search-hits__enter">
              <IconDownload />
            </div>
          </a>
        )}
        {hit.datasheet && (
          <a href={hit.datasheet} ref={cardRef} className={'search-hits'} download target="_blank" rel="noreferrer">
            <div className="search-hits__img">{hit.slug ? getIcon(hit.slug.split('/')[1]) : getIcon(null)}</div>
            <div className="search-hits__content">
              <div className="search-hits__title">{hit.title} - Datasheet</div>
              <div className="search-hits__description">{hit.description}</div>
              <div className="search-hits__breadcrumbs">
                <span>
                  Home
                  <span className="search-hits__breadcrumbs--separator">{' / '}</span>
                  Hardware
                  <span className="search-hits__breadcrumbs--separator">{' / '}</span>
                  {hit.title}
                </span>
              </div>
            </div>
            <div className="search-hits__enter">
              <IconDownload />
            </div>
          </a>
        )}
        {hit.schematic && (
          <a href={hit.schematic} ref={cardRef} className={'search-hits'} download target="_blank" rel="noreferrer">
            <div className="search-hits__img">{hit.slug ? getIcon(hit.slug.split('/')[1]) : getIcon(null)}</div>
            <div className="search-hits__content">
              <div className="search-hits__title">{hit.title} - Schematic</div>
              <div className="search-hits__description">{hit.description}</div>
              <div className="search-hits__breadcrumbs">
                <span>
                  Home
                  <span className="search-hits__breadcrumbs--separator">{' / '}</span>
                  Hardware
                  <span className="search-hits__breadcrumbs--separator">{' / '}</span>
                  {hit.title}
                </span>
              </div>
            </div>

            <div className="search-hits__enter">
              <IconDownload />
            </div>
          </a>
        )}
      </>
    );
  }

  return (
    <Link
      to={hit.slug}
      ref={cardRef}
      onClick={() => saveRecentSearch(hit)}
      className={classnames('search-hits', {
        'search-hits--selected': selectedPanelItem === index,
      })}
    >
      <div className="search-hits__img">{hit.slug ? getIcon(hit.slug.split('/')[1]) : getIcon(null)}</div>
      <div className="search-hits__content">
        <div className="search-hits__title">{hit.title}</div>
        <div className="search-hits__description">{hit.description}</div>
        {/* FIXME - use component */}
        {showBreadcrumbs && hit.slug?.split('/')[1] !== PRODUCT_PATH && hit.slug?.split('/')[1] !== SOFTWARE_PATH && (
          <div className="search-hits__breadcrumbs">
            Home
            <span className="search-hits__breadcrumbs--separator">{' / '}</span>
            {hit.slug.substring(0, 11) === '/tutorials/' && (
              <>
                <span>Hardware</span>
                <span className="search-hits__breadcrumbs--separator">{' / '}</span>
              </>
            )}
            {hit.breadcrumbObj?.map((breadcrumb, i) => (
              <span key={breadcrumb.slug}>
                {breadcrumb.title === '' ? getDocumentationType(hit.documentation_type) : breadcrumb.title}{' '}
                <span className="search-hits__breadcrumbs--separator">{'/ '}</span>
                {i === hit.breadcrumbObj.length - 1 && hit.title}
              </span>
            )) || hit.title}
          </div>
        )}
        {(hit.slug?.split('/')[1] === PRODUCT_PATH || hit.slug?.split('/')[1] === SOFTWARE_PATH) && (
          <div className="search-hits__breadcrumbs">
            <span>
              Home
              <span className="search-hits__breadcrumbs--separator">{' / '}</span>
              {hit.slug?.split('/')[1] === PRODUCT_PATH ? 'Hardware' : 'Software'}
              <span className="search-hits__breadcrumbs--separator">{' / '}</span>
              {hit.title}
            </span>
          </div>
        )}
      </div>
      {selectedPanelItem === index && (
        <div className="search-hits__enter">
          <IconEnter />
        </div>
      )}
    </Link>
  );
};

const SearchBarHits = ({ hits, saveRecentSearch, selectedPanelItem, setSelectedPanelSlug, actualPage, selectedPage }: SearchBarHitsProps) => {
  const ref = useRef();
  //console.log('-------SearchBarHits-------: hits', hits);
  //console.log('-------SearchBarHits-------: hits', hits.length);
  //console.log('-------SearchBarHits-------: actualPage', actualPage, selectedPage);

  const noTutorials = hits.filter((allHits) =>
    actualPage.title === selectedPage && selectedPage !== ALL_DOCS
      ? selectedPage === 'Hardware'
        ? allHits.slug?.split('/')[1] === actualPage.url.replace(/(^\/+|\/+$)/gm, '') || allHits.slug?.split('/')[1] === 'tutorials'
        : allHits.slug?.split('/')[1] === actualPage.url.replace(/(^\/+|\/+$)/gm, '')
      : allHits
  ).length;
  return (
    <>
      {hits
        .filter((allHits) =>
          actualPage.title === selectedPage && selectedPage !== ALL_DOCS
            ? selectedPage === 'Hardware'
              ? allHits.slug?.split('/')[1] === actualPage.url.replace(/(^\/+|\/+$)/gm, '') || allHits.slug?.split('/')[1] === 'tutorials'
              : allHits.slug?.split('/')[1] === actualPage.url.replace(/(^\/+|\/+$)/gm, '')
            : allHits
        )
        .map((hit, i) => {
          return i < LIMIT_HITS_PANEL ? (
            <>
              <SearchBarHit
                setSelectedPanelSlug={setSelectedPanelSlug}
                index={i}
                hit={hit}
                key={hit.slug}
                saveRecentSearch={saveRecentSearch}
                selectedPanelItem={selectedPanelItem}
                showBreadcrumbs={selectedPage === ALL_DOCS}
              />
            </>
          ) : null;
        })}
      {(hits.length === 0 || noTutorials === 0) && <span className="search-hits__no-tutorials">There are no tutorials that match the search.</span>}
      {hits && <div style={{ width: '100%' }} ref={ref}></div>}
    </>
  );
};

export default connectInfiniteHits(SearchBarHits);
